import type { FormMode } from '@cod/config-schema';
import { Box, Card, Text } from '@nimbus-ds/components';
import { CloseIcon, InfoCircleIcon } from '@nimbus-ds/icons';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Card-style display-mode selector: two large, clickable option cards (Popup / Embedded)
 * with a contextual info banner below that explains the selected mode. Replaces the plain
 * tab strip with the richer Releasit-style picker.
 */

function PopupGlyph(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="#2b2b2b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 8V6a3 3 0 0 1 3-3h2" />
      <path d="M16 3h2a3 3 0 0 1 3 3v2" />
      <path d="M21 16v2a3 3 0 0 1-3 3h-2" />
      <path d="M8 21H6a3 3 0 0 1-3-3v-2" />
      <rect x="8.5" y="8.5" width="7" height="7" rx="2" fill="#2b2b2b" stroke="none" />
    </svg>
  );
}

function EmbeddedGlyph(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="#2b2b2b"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 4h14v9l-6 7H5z" />
      <path d="M19 13h-6v7" />
    </svg>
  );
}

function ModeCard({
  selected,
  label,
  glyph,
  onClick,
}: {
  selected: boolean;
  label: string;
  glyph: JSX.Element;
  onClick: () => void;
}): JSX.Element {
  return (
    <div
      role="button"
      aria-pressed={selected}
      onClick={onClick}
      style={{
        flex: 1,
        cursor: 'pointer',
        borderRadius: 8,
        overflow: 'hidden',
        border: `2px solid ${selected ? '#4a4a4a' : '#e3e3e6'}`,
        boxShadow: selected ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
        transition: 'border-color .12s ease, box-shadow .12s ease',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px 0',
          background: selected ? '#d4d4d6' : '#fbfbfc',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 8,
            background: '#ffffff',
            border: '1px solid rgba(0,0,0,.08)',
            boxShadow: '0 1px 2px rgba(0,0,0,.14)',
          }}
        >
          {glyph}
        </div>
      </div>
      <div
        style={{
          padding: '8px 0',
          textAlign: 'center',
          background: '#ffffff',
          borderTop: '1px solid rgba(0,0,0,.10)',
        }}
      >
        <Text fontWeight="bold" textAlign="center">
          {label}
        </Text>
      </div>
    </div>
  );
}

export function ModeSwitch({
  mode,
  onChange,
}: {
  mode: FormMode;
  onChange: (mode: FormMode) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  const select = (next: FormMode): void => {
    if (next !== mode) setDismissed(false);
    onChange(next);
  };

  return (
    <Card>
      <Card.Body>
        <Box display="flex" gap="4">
          <ModeCard
            selected={mode === 'popup'}
            label={t('designer.modePopup')}
            glyph={<PopupGlyph />}
            onClick={() => select('popup')}
          />
          <ModeCard
            selected={mode === 'embedded'}
            label={t('designer.modeEmbedded')}
            glyph={<EmbeddedGlyph />}
            onClick={() => select('embedded')}
          />
        </Box>

        {!dismissed && (
          <Box
            marginTop="3"
            display="flex"
            gap="2"
            padding="3"
            borderRadius="2"
            backgroundColor="primary-surface"
          >
            <span style={{ color: '#2e5aac', display: 'flex', flex: '0 0 auto', marginTop: 1 }}>
              <InfoCircleIcon size="small" />
            </span>
            <Box flex="1">
              <Text fontSize="caption" color="primary-textHigh">
                <Trans
                  i18nKey={mode === 'popup' ? 'designer.popupNote' : 'designer.embeddedNote'}
                  components={{ b: <strong /> }}
                />
              </Text>
            </Box>
            <button
              type="button"
              aria-label={t('common.close')}
              onClick={() => setDismissed(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#2e5aac',
                display: 'flex',
                flex: '0 0 auto',
                padding: 0,
              }}
            >
              <CloseIcon size="small" />
            </button>
          </Box>
        )}
      </Card.Body>
    </Card>
  );
}
